import type { DeckShare, DeckShareView } from '@/config/objects/deck-shares';
import { EmptyTableState, formatDateTime } from '@startupquickstart/frontend';
import { EyeOff } from 'lucide-react';

interface DeckShareActivityCardProps {
  share: DeckShare;
}

/**
 * Condenses a user-agent string into something readable in a table cell.
 * Approximate by design — this answers "phone or laptop?", not forensics.
 */
function describeDevice(userAgent: string | null | undefined): string {
  if (!userAgent) return 'Unknown device';
  if (/iPhone|iPod/i.test(userAgent)) return 'iPhone';
  if (/iPad/i.test(userAgent)) return 'iPad';
  if (/Android/i.test(userAgent)) return 'Android';
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'Mac';
  if (/Windows/i.test(userAgent)) return 'Windows';
  return 'Other';
}

/**
 * The view log for a share: every sitting, most recent first.
 *
 * Each row is a viewing SESSION, not a page load — repeat opens inside a
 * half-hour fold together (see `VIEW_SESSION_WINDOW_MS` in the API). Without
 * that, a prospect who refreshed twice would look like three separate visits.
 */
export function DeckShareActivityCard({ share }: DeckShareActivityCardProps) {
  const views: DeckShareView[] = share.views ?? [];

  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <div className="border-b px-6 py-4">
        <h2 className="text-lg font-medium tracking-tight text-zinc-900">Activity</h2>
        <p className="text-sm text-muted-foreground">
          {share.viewCount === 0
            ? 'Not opened yet'
            : `Opened ${share.viewCount} ${share.viewCount === 1 ? 'time' : 'times'}${
                share.lastViewedAt ? ` · last ${formatDateTime(share.lastViewedAt)}` : ''
              }`}
        </p>
      </div>

      <div className="p-6">
        {views.length === 0 ? (
          <EmptyTableState
            icon={<EyeOff className="h-6 w-6" />}
            title="No opens recorded"
            description="You'll get an email the first time this link is opened."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 pr-4 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                    Opened
                  </th>
                  <th className="pb-2 pr-4 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                    Device
                  </th>
                  <th className="pb-2 pr-4 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                    Pages loaded
                  </th>
                  <th className="pb-2 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                    IP
                  </th>
                </tr>
              </thead>
              <tbody>
                {views.map((view) => (
                  <tr key={view.id} className="border-b last:border-0">
                    <td className="py-2.5 pr-4 whitespace-nowrap">
                      {formatDateTime(view.viewedAt)}
                    </td>
                    <td className="py-2.5 pr-4" title={view.userAgent ?? undefined}>
                      {describeDevice(view.userAgent)}
                    </td>
                    <td className="py-2.5 pr-4">{view.requestCount}</td>
                    <td className="py-2.5 text-muted-foreground">{view.ipAddress ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
